import { React, useState, useEffect, useRef, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { setCartItems } from "../../store/actions";
import { main_paige_title } from "../../constants/constants";
import { getProducts } from "../../constants/products";
import Header from "../../components/header/Header";
import PrevAndNextItemNavigation from "../../components/navigation/PrevAndNextItemNavigation";
import ProductImagesSlideshow from "../../components/slideshow/ProductImagesSlideshow";
import Footer from "../../components/footer/Footer";

const settings = {
  slidesToScroll: 1,
  slidesToShow: 1,
  adaptiveHeight: true,
  arrows: true,
  dots: true,
  autoplay: false,
  autoplaySpeed: 3000,
  centerMode: false,
  draggable: true,
  fade: true,
  focusOnSelect: false,
  infinite: true,
  speed: 300,
};

const main_collapsed_class =
  " nav-link fs-lg-32 fs-24 font-weight-600 p-0 mr-md-10 mr-4 ";

const Product = () => {
  const dispatch = useDispatch();
  const targetRef = useRef();

  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    if (targetRef.current) {
      if (window.innerWidth < 768) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    }
  }, []);

  const cartItems = useSelector((state) => state.cartItems);
  const { state } = useLocation();
  const { allProducts } = state || {};
  
  // States for current product, previous product, and next product
  const [product, setProduct] = useState(false);
  const [prevProduct, setPrevProduct] = useState(null);
  const [nextProduct, setNextProduct] = useState(null);

  const [showDescription, setShowDescription] = useState(true);
  const [descriptionClass, setDescriptionClass] = useState(
    main_collapsed_class + " active"
  );
  const [showInformation, setShowInformation] = useState(false);
  const [informationClass, setInformationClass] = useState(main_collapsed_class);

  function triggerDescription(e) {
    e.preventDefault();
    setShowDescription(true);
    setShowInformation(false);
    setDescriptionClass(main_collapsed_class + " active");
    setInformationClass(main_collapsed_class);
  }

  function triggerInformation(e) {
    e.preventDefault();
    setShowDescription(false);
    setShowInformation(true);
    setDescriptionClass(main_collapsed_class);
    setInformationClass(main_collapsed_class + " active");
  }

  function btnAddToCart(e, element) {
    // Check if the product is already in the cart
    const productInCart = cartItems.find(
      (x) => x.product_id === element.product_id
    );
    // If the product doesn't exist in the cart, add it
    if (!productInCart) {
      element.product_quantity = 1;
      dispatch(setCartItems([...cartItems, element]));
    }
  }

  useEffect(() => {
    const product_slug = window.location.href.split("product/").pop();
    if (!product_slug) {
      window.location.href = "/";
    }

    // If you already have allProducts passed in via state:
    //   We can just use 'allProducts' instead of getProducts().
    // Otherwise, fallback to getProducts() or fetch from your API:
    const products = allProducts && allProducts.length
      ? allProducts
      : getProducts();

    const data = products.find((x) => x.product_slug === product_slug);
    setProduct(data || false);

    if (data && products.length) {
      // Find the index of the current product based on product_id
      const currentIndex = products.findIndex(
        (item) => item.product_id === data.product_id
      );

      // Set previous product (null if none exists)
      if (currentIndex > 0) {
        setPrevProduct(products[currentIndex - 1]);
      } else {
        setPrevProduct(null);
      }

      // Set next product (null if none exists)
      if (currentIndex < products.length - 1) {
        setNextProduct(products[currentIndex + 1]);
      } else {
        setNextProduct(null);
      }
    }
  }, [allProducts]);

  if (product === false) {
    return "Loading...";
  }

  const original_price = Number(product.product_price);
  const discount = Number(product.product_discount);
  const new_price = Number(original_price - (discount * original_price) / 100).toFixed(2);
  const price_with_discount = discount ? new_price : original_price;

  return (
    <div>
      <Helmet>
        <title property="title">
          {main_paige_title} - Produkt - {product.product_name}
        </title>
      </Helmet>
      <Header />
      <main id="content" ref={targetRef}>
        {/* Breadcrumb Section */}
        <section className="bg-color-3 mt-10">
          <div className="container">
            <nav
              aria-label="breadcrumb"
              className="d-flex align-items-center justify-content-between"
            >
              <ol className="breadcrumb py-3 mr-6">
                <li className="breadcrumb-item">
                  <Link to="/">Startseite</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/shop">Shop</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {product.product_name}
                </li>
              </ol>

              {/* Previous / Next navigation */}
              <div className="navigation d-flex align-items-center">
                <PrevAndNextItemNavigation direction="prev" product={prevProduct}/>
                <PrevAndNextItemNavigation direction="next" product={nextProduct}/>
              </div>
            </nav>
          </div>
        </section>
        {/* Product Section */}
        <section className="pt-10 pb-lg-15 pb-11">
          <div className="container">
            <div className="row no-gutters">
              {/* Product Images */}
              <div className="col-md-8 mb-6 mb-md-0 pr-md-6 pr-lg-9">
                <ProductImagesSlideshow product={product} />
              </div>
              {/* Product Info */}
              <div className="col-md-4">
                <p className="text-muted fs-12 font-weight-500 letter-spacing-05 text-uppercase mb-3">
                  {product.category}
                </p>
                <h2 className="fs-30 fs-lg-40 mb-2">{product.product_name}</h2>
                {discount > 0 && (
                  <p className="fs-20 text-secondary  mb-4">
                    <span className="mr-5 text-danger">-{discount} %</span>
                    <span className="text-line-through">
                      {original_price.toFixed(2)} EUR
                    </span>
                  </p>
                )}
                <p className="fs-20 text-primary mb-4">
                  {price_with_discount.toFixed(2)} EUR
                </p>
                <p className="mt-6 mt-md-10 mxw-830 mx-auto mb-10">
                  {product.short_description}
                </p>

                <button
                  type="button"
                  onClick={(e) => btnAddToCart(e, product)}
                  className="btn btn-primary btn-block mb-4"
                >
                  In den Warenkorb
                </button>
                <ul className="list-inline px-xl-8 mb-4 d-flex align-items-center justify-content-center">
                  <li className="list-inline-item mr-5">
                    <img className="" src="../images/p1.png" alt="Visa" />
                  </li>
                  <li className="list-inline-item mr-5">
                    <img className="" src="../images/p2.png" alt="Visa" />
                  </li>
                  <li className="list-inline-item mr-5">
                    <img className="" src="../images/p3.png" alt="Visa" />
                  </li>
                  <li className="list-inline-item mr-5">
                    <img className="" src="../images/p4.png" alt="Visa" />
                  </li>
                  <li className="list-inline-item mr-5">
                    <img className="" src="../images/p5.png" alt="Visa" />
                  </li>
                </ul>
                <p className="d-flex text-primary justify-content-center">
                  <span className="d-inline-block mr-2 fs-14">
                    <i className="far fa-lock"></i>
                  </span>
                  <span className="fs-15">
                    Guarantee Safe and Secure Checkout
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Description Section */}
        <section className="pb-11 pb-lg-13">
          <div className="container">
            <div className="collapse-tabs">
              <ul className="nav nav-pills mb-3 justify-content-center d-md-flex ">
                <li className="nav-item">
                  <a
                    className={descriptionClass}
                    href="/#"
                    onClick={triggerDescription}
                  >
                    Artikelbeschreibung
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={informationClass}
                    href="/#"
                    onClick={triggerInformation}
                  >
                    Information
                  </a>
                </li>
              </ul>
              <div className="tab-content bg-white-md shadow-none py-md-5 p-0">
                <div>
                  {/* Description */}
                  {showDescription && (
                    <div
                      id="description-collapse-01"
                      className="collapsible collapse show"
                      aria-labelledby="headingDetails-01"
                      data-parent="#collapse-tabs-accordion-01"
                    >
                      <div
                        id="accordion-style-01"
                        className="accordion accordion-01 border-md-0 border p-md-0 p-6"
                      >
                        <p
                          className="mt-6 mt-md-10 mxw-830 mx-auto mb-0"
                          dangerouslySetInnerHTML={{
                            __html: product?.description || "Keine Artikelbeschreibung",
                          }}
                        ></p>
                      </div>
                    </div>
                  )}
                  {/* Information */}
                  {showInformation && (
                    <div className="accordion accordion-01 border-md-0 border p-md-0 p-6">
                      <div className="mxw-830 mx-auto pt-md-4">
                        <div className="table-responsive mb-md-7">
                          {product.specifications.rows && (
                            <table className="table table-border-top-0 mb-0">
                              <tbody>
                                {product.specifications.rows.map(
                                  (element, index) => {
                                    return (
                                      <tr key={index}>
                                        <td className="pl-0">{element.name}</td>
                                        <td className="text-right pr-0">
                                          {element.value}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                              </tbody>
                            </table>
                          )}
                        </div>
                        <div className="row">
                          {product.specifications.description}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Product;